import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { FileUp, File, Trash2, Eye, Loader2, FileText, Image, X, Lock, Ticket as TicketIcon, AlertTriangle, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getFileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.includes('pdf')) return FileText;
  return File;
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

export const DocumentUpload = forwardRef(({ contractId, ticketId }, ref) => {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const entityId = contractId || ticketId;
  const entityType = contractId ? 'contract' : 'ticket';

  // Immutability: non super_admin cannot upload/delete on already-saved contracts
  const isSuperAdmin = user?.role === 'super_admin';
  const isContractContext = !!contractId;
  const locked = isContractContext && !isSuperAdmin;

  useImperativeHandle(ref, () => ({
    uploadPendingFiles: async (newContractId) => {
      if (pendingFiles.length === 0) return;
      for (const file of pendingFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('contract_id', newContractId);
        // Let axios set the multipart boundary automatically
        await axios.post(`${API_URL}/files/upload`, formData);
      }
      setPendingFiles([]);
    },
    hasPendingFiles: () => pendingFiles.length > 0,
  }));

  const fetchFiles = useCallback(async () => {
    if (!entityId) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API_URL}/files/${entityType}/${entityId}`);
      setFiles(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    if (!entityId) {
      setPendingFiles(prev => [...prev, ...selectedFiles]);
      toast.success(`${selectedFiles.length} ficheiro(s) adicionado(s). Serão carregados ao gravar.`);
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        if (contractId) formData.append('contract_id', contractId);
        if (ticketId) formData.append('ticket_id', ticketId);
        // Let axios set the multipart boundary automatically
        await axios.post(`${API_URL}/files/upload`, formData);
      }
      toast.success(`${selectedFiles.length} ficheiro(s) carregado(s)`);
      fetchFiles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao carregar ficheiro');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Eliminar ficheiro?')) return;
    try {
      await axios.delete(`${API_URL}/files/${fileId}`);
      toast.success('Ficheiro eliminado');
      fetchFiles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao eliminar');
    }
  };

  const handleView = (fileId) => {
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/files/${fileId}/download?auth=${token}`, '_blank');
  };

  const handleReplace = async (fileId, fileList) => {
    const replacement = fileList?.[0];
    if (!replacement) return;
    const formData = new FormData();
    formData.append('file', replacement);
    try {
      await axios.post(`${API_URL}/files/${fileId}/replace`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Ficheiro substituído com sucesso');
      fetchFiles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao substituir ficheiro');
    }
  };

  const canReplace = user?.role === 'super_admin' || user?.role === 'gestor' || (user?.role === 'admin' && (user?.permissions?.editar_estado_inline === true));

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Most recent file overall (any origin) - that's the one with "MAIS RECENTE" badge.
  const mostRecentFile = files
    .slice()
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0];

  return (
    <div className="space-y-4" data-testid="document-upload-section">
      {/* Locked notice for non super_admin on existing contract */}
      {locked && (
        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="documents-locked-notice">
          <Lock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800 leading-relaxed">
            <p className="font-semibold mb-0.5">Documentos imutáveis</p>
            <p>Após a criação do contrato, não é possível adicionar ou eliminar documentos directamente. Para enviar ficheiros adicionais, abra um <span className="inline-flex items-center gap-1 font-medium"><TicketIcon className="w-3 h-3" />Ticket</span> e anexe o ficheiro. Será automaticamente associado a este contrato.</p>
          </div>
        </div>
      )}

      {/* Upload button (hidden when locked) */}
      {!locked && (
        <div className="flex items-center gap-3">
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              className="hidden"
              onChange={handleUpload}
              accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv"
              data-testid="file-upload-input"
            />
            <Button type="button" variant="outline" className="gap-2 rounded-full" disabled={uploading} asChild>
              <span>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                {uploading ? 'A carregar...' : 'Carregar ficheiro'}
              </span>
            </Button>
          </label>
          <span className="text-xs text-slate-400">PDF, imagens, documentos (máx 10MB)</span>
        </div>
      )}

      {/* Pending files (not yet uploaded - new contract) */}
      {pendingFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-amber-600">Ficheiros pendentes (serão carregados ao gravar)</p>
          {pendingFiles.map((file, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`pending-file-${index}`}>
              <File className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{file.name}</p>
                <p className="text-xs text-slate-400">{formatSize(file.size)}</p>
              </div>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removePendingFile(index)} data-testid={`remove-pending-${index}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded files */}
      {entityId && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : files.length === 0 ? (
            <p className="text-sm text-slate-400 py-2">Nenhum documento carregado</p>
          ) : (
            <div className="space-y-2">
              {files.map((file) => {
                const Icon = getFileIcon(file.content_type);
                const isMostRecent = mostRecentFile && file.id === mostRecentFile.id;
                const isCorrupt = !!file.is_corrupt;
                return (
                  <div key={file.id} className={`flex items-center gap-3 p-3 rounded-lg group ${isCorrupt ? 'bg-red-50 border border-red-200' : isMostRecent ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'}`} data-testid={`file-item-${file.id}`}>
                    {isCorrupt ? (
                      <AlertTriangle className="w-5 h-5 flex-shrink-0 text-red-600" />
                    ) : (
                      <Icon className={`w-5 h-5 flex-shrink-0 ${isMostRecent ? 'text-emerald-600' : 'text-[#2D3E6F]'}`} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-slate-700 truncate">{file.filename}</p>
                        {isCorrupt && (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-red-600 text-white px-2 py-0.5 rounded-full" data-testid={`corrupt-badge-${file.id}`}>
                            Corrompido
                          </span>
                        )}
                        {!isCorrupt && isMostRecent && (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-600 text-white px-2 py-0.5 rounded-full" data-testid={`most-recent-badge-${file.id}`}>
                            Mais recente
                          </span>
                        )}
                        {file.via_ticket && (
                          <span className="text-[10px] font-medium uppercase tracking-wide bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                            Via ticket
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400">
                        {formatSize(file.size)}
                        {file.created_at && <span className="ml-2">· {formatDateTime(file.created_at)}</span>}
                      </p>
                      {isCorrupt && (
                        <p className="text-xs text-red-700 mt-1">
                          Ficheiro indisponível no storage. {canReplace ? 'Clique em "Reenviar" para substituir.' : 'Contacte um gestor para reenviar o documento.'}
                        </p>
                      )}
                    </div>
                    <div className={`flex items-center gap-1 ${isCorrupt ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                      {isCorrupt && canReplace && (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => handleReplace(file.id, e.target.files)}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt,.webp"
                            data-testid={`replace-input-${file.id}`}
                          />
                          <Button type="button" variant="outline" size="sm" className="h-8 gap-1 border-red-300 text-red-700 hover:bg-red-100" asChild data-testid={`replace-file-${file.id}`}>
                            <span>
                              <Upload className="w-3.5 h-3.5" />
                              Reenviar
                            </span>
                          </Button>
                        </label>
                      )}
                      {!isCorrupt && (
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleView(file.id)} data-testid={`view-file-${file.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      {!locked && !isCorrupt && (
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDelete(file.id)} data-testid={`delete-file-${file.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Empty state for new contracts with no pending files */}
      {!entityId && pendingFiles.length === 0 && (
        <p className="text-sm text-slate-400 py-2">Selecione ficheiros para carregar com o contrato</p>
      )}
    </div>
  );
});

DocumentUpload.displayName = 'DocumentUpload';
